"use client";

import { ExternalLink } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";

import { useRouter } from "@/i18n/navigation";
import { getProject } from "@/lib/api";
import { ProjectSummary } from "@/types/project";

type PreviewSlide = {
  type: "image" | "video";
  url: string;
};

function normalizePreviewMediaUrl(url: string): string {
  try {
    const parsedUrl = new URL(url, window.location.origin);

    if (parsedUrl.pathname.startsWith("/api/media/")) {
      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }

    return parsedUrl.toString();
  } catch {
    return url;
  }
}

export function ProjectCard({ project }: { project: ProjectSummary }) {
  const t = useTranslations("ProjectCard");
  const locale = useLocale();
  const router = useRouter();
  const [iconSrc, setIconSrc] = useState(project.favicon_url ?? project.favicon_fallback_url ?? "");
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const [previewSlides, setPreviewSlides] = useState<PreviewSlide[]>([]);
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [utilityScore, setUtilityScore] = useState<number | null>(null);

  const currentSlide = useMemo(() => {
    if (previewSlides.length === 0) {
      return null;
    }

    return previewSlides[activePreviewIndex] ?? previewSlides[0];
  }, [activePreviewIndex, previewSlides]);
  const formattedLastUpdated = useMemo(() => {
    if (!lastUpdatedAt) {
      return null;
    }

    const parsedDate = new Date(lastUpdatedAt);
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    const day = String(parsedDate.getDate()).padStart(2, "0");
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const year = String(parsedDate.getFullYear());

    return `${day}-${month}-${year}`;
  }, [lastUpdatedAt]);
  const utilityStars = useMemo(() => {
    if (typeof utilityScore !== "number") {
      return null;
    }

    const clamped = Math.max(0, Math.min(5, utilityScore));
    return Math.round(clamped * 2) / 2;
  }, [utilityScore]);
  const shouldShowPreview = true;

  useEffect(() => {
    if (!shouldShowPreview || !isPreviewLoading || previewSlides.length > 0) {
      return;
    }

    let isCancelled = false;

    void getProject(project.id)
      .then((detail) => {
        if (isCancelled) {
          return;
        }

        const slides: PreviewSlide[] = [
          ...detail.image_urls.map((url) => ({ type: "image" as const, url: normalizePreviewMediaUrl(url) })),
          ...detail.video_urls.map((url) => ({ type: "video" as const, url: normalizePreviewMediaUrl(url) })),
        ];

        setPreviewSlides(slides);
        setActivePreviewIndex(0);
        setLastUpdatedAt(detail.created_at ?? null);
        setUtilityScore(typeof detail.aggregate_utility_score === "number" ? detail.aggregate_utility_score : null);
      })
      .catch(() => {
        if (!isCancelled) {
          setPreviewSlides([]);
          setLastUpdatedAt(null);
          setUtilityScore(null);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsPreviewLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [isPreviewLoading, previewSlides.length, project.id, shouldShowPreview]);

  useEffect(() => {
    if (!shouldShowPreview || previewSlides.length < 2) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setActivePreviewIndex((previous) => (previous + 1) % previewSlides.length);
    }, 2600);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [previewSlides.length, shouldShowPreview]);

  return (
    <article
      role="link"
      tabIndex={0}
      onClick={() => router.push(`/projects/${project.id}`)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) {
          return;
        }

        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          router.push(`/projects/${project.id}`);
        }
      }}
      className="group cursor-pointer rounded-2xl border border-zinc-300/90 bg-white/85 p-3 pb-2 px-2 transition hover:border-zinc-400 hover:bg-white sm:p-2 sm:px-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500 dark:border-zinc-800/80 dark:bg-zinc-900/55 dark:hover:border-zinc-700 dark:hover:bg-zinc-900/70 dark:focus-visible:ring-zinc-300"
    >
      <div className="min-w-0">
        <div className="flex items-start gap-3">
          {iconSrc ? (
            // Small external favicon is intentionally rendered as a plain image.
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={iconSrc}
              alt={`${project.title} icon`}
              className="mt-0.5 h-9 w-9 rounded-full border border-zinc-300 bg-white p-1 dark:border-zinc-700 dark:bg-zinc-950"
              onError={() => {
                if (iconSrc !== project.favicon_fallback_url && project.favicon_fallback_url) {
                  setIconSrc(project.favicon_fallback_url);
                } else {
                  setIconSrc("");
                }
              }}
            />
          ) : (
            <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full border border-zinc-300 bg-white text-xs font-semibold text-zinc-600 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-400">
              {project.title.slice(0, 1).toUpperCase()}
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 min-h-14">
                <h3 className="truncate text-lg font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">{project.title}</h3>
                <p className="line-clamp-2 text-xs text-zinc-600 dark:text-zinc-400">{project.tagline}</p>
              </div>

              <a
                href={project.external_url}
                target="_blank"
                rel="noreferrer"
                aria-label={`Visit ${project.title}`}
                title={t("visit")}
                onClick={(event) => event.stopPropagation()}
                className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-zinc-300 bg-transparent text-zinc-700 transition hover:border-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 dark:border-zinc-700 dark:text-zinc-300 dark:hover:border-zinc-500 dark:hover:bg-zinc-900/80 dark:hover:text-zinc-100"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`grid transition-[grid-template-rows,margin-top] duration-300 ease-out ${
          shouldShowPreview ? "mt-4 grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="relative aspect-video overflow-hidden rounded-xl bg-zinc-100 dark:bg-zinc-950">
            {isPreviewLoading && !currentSlide ? <div className="h-full w-full animate-pulse bg-zinc-200 dark:bg-zinc-900" /> : null}

            {!isPreviewLoading ? (
              currentSlide ? (
                currentSlide.type === "image" ? (
                // Preview media can come from external sources; keep rendering flexible.
                // eslint-disable-next-line @next/next/no-img-element
                  <img src={currentSlide.url} alt={`${project.title} preview`} className="h-full w-full object-cover" />
                ) : (
                  <video
                    src={currentSlide.url}
                    className="h-full w-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                  />
                )
              ) : (
                <div className="flex h-full w-full items-center justify-center text-sm text-zinc-600 dark:text-zinc-400">No preview available</div>
              )
            ) : null}

            {previewSlides.length > 1 ? (
              <p className="absolute bottom-2 right-2 rounded-full border border-zinc-300 bg-white/90 px-2 py-0.5 text-[10px] text-zinc-700 backdrop-blur dark:border-zinc-700 dark:bg-zinc-950/80 dark:text-zinc-200">
                {activePreviewIndex + 1}/{previewSlides.length}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {project.action_tags.length > 0 ? (
        <div className="mt-3 overflow-x-auto">
          <div className="flex min-w-max items-center gap-2 pr-1">
            {project.action_tags.map((tag) => (
              <span key={tag} className="whitespace-nowrap rounded-md bg-zinc-200 px-2 py-1 text-xs text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                {tag}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {utilityStars !== null || formattedLastUpdated ? (
        <div className="mt-2 flex items-center justify-between gap-3">
          {utilityStars !== null ? (
            <div
              className="flex items-center gap-1.5"
              aria-label={t("utilityRatingAria", { rating: utilityScore?.toFixed(1) ?? "0.0" })}
              title={t("utilityRatingAria", { rating: utilityScore?.toFixed(1) ?? "0.0" })}
            >
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, index) => {
                  const starNumber = index + 1;
                  const isFull = utilityStars >= starNumber;
                  const isHalf = !isFull && utilityStars >= starNumber - 0.5;

                  return (
                    <span key={starNumber} className="relative inline-block h-3.5 w-3.5 text-[13px] leading-none">
                      <span className="absolute left-0 top-0 text-zinc-300 dark:text-zinc-700">★</span>
                      {isFull ? <span className="absolute left-0 top-0 text-amber-500">★</span> : null}
                      {isHalf ? <span className="absolute left-0 top-0 w-1/2 overflow-hidden text-amber-500">★</span> : null}
                    </span>
                  );
                })}
              </div>
              <span className="text-xs text-zinc-600 dark:text-zinc-400">{utilityScore?.toFixed(1)}/5</span>
            </div>
          ) : (
            <span className="text-xs text-zinc-500 dark:text-zinc-400">{t("updated", { date: formattedLastUpdated ?? "--" })}</span>
          )}

          {formattedLastUpdated ? (
            <span className="text-xs text-zinc-500 dark:text-zinc-400">{t("updated", { date: formattedLastUpdated })}</span>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
